import {
  ChangelogEntry,
  ChangelogEntryImage,
  ChangelogEntryLink,
} from '@/Domain/api/Changelog/dto/ChangelogEntry.dto';

// Shared with resolveImageSrc() so the two cannot drift: window.open() must never get a javascript: url.
export const isAbsoluteHttpUrl = (value: unknown): value is string =>
  typeof value === 'string' && /^https?:\/\//i.test(value);

// resolveImageSrc() prefixes a site-relative src with the feed's own origin before it reaches <img>.
const isAssetUrl = (value: unknown): value is string =>
  isAbsoluteHttpUrl(value) || (typeof value === 'string' && value.startsWith('/'));

// Intl.DateTimeFormat.format() throws RangeError on a date string it cannot parse.
const isRenderableDate = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(new Date(value).getTime());

const isChangelogEntryImage = (value: unknown): value is ChangelogEntryImage | null => {
  if (value === null) return true;
  if (!value || typeof value !== 'object') return false;
  const image = value as Record<string, unknown>;
  return isAssetUrl(image['src']) && typeof image['alt'] === 'string';
};

const isChangelogEntryLink = (value: unknown): value is ChangelogEntryLink => {
  if (!value || typeof value !== 'object') return false;
  const link = value as Record<string, unknown>;
  return typeof link['label'] === 'string' && isAbsoluteHttpUrl(link['href']);
};

export const isChangelogEntry = (value: unknown): value is ChangelogEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry['id'] === 'string' &&
    typeof entry['title'] === 'string' &&
    typeof entry['description'] === 'string' &&
    isRenderableDate(entry['date']) &&
    isAbsoluteHttpUrl(entry['url']) &&
    isChangelogEntryImage(entry['image']) &&
    Array.isArray(entry['links']) &&
    entry['links'].every(isChangelogEntryLink) &&
    Array.isArray(entry['tags']) &&
    entry['tags'].every((tag: unknown) => typeof tag === 'string')
  );
};
